import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { TrashIcon } from '@heroicons/react/24/outline';

const ItemTypes = {
  ROW: 'row'
};

const DraggableRow = ({
  index,
  item,
  access,
  moveRow,
  isLastElement,
  dataIsLoading,
  rowType = 'bom',
  handleUpdateItem,
  handleRemoveRow,
  handleDeleteItem,
  disableItems,
  textareaRefs,
  totalRows,
}) => {
  const ref = React.useRef(null);

  const isEmpty = (item) => {
    switch (rowType) {
      case 'moq':
        return item.cost === 0 && item.quantity === 1;
      case 'bom':
        return !item.component && !item.description && item.cost === 0 && item.quantity === 1;
      case 'instructions':
      case 'materials':
        return !item.title && !item.description;
      default:
        return true;
    }
  };

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ROW,
    item: () => {
      if (isEmpty(item)) {
        return null;
      }
      return { index, isEmpty: isEmpty(item) };
    },
    canDrag: () => access === "edit" && !isEmpty(item),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.ROW,
    hover(draggedItem) {
      if (!ref.current || !draggedItem) return;
      
      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;
      if (isEmpty(item)) return;

      moveRow(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    }
  });

  const handleTab = (e) => {
    if (rowType !== 'bom' && rowType !== 'moq' && e.key === 'Tab') {
      e.preventDefault();
      const nextIndex = e.shiftKey ? index - 1 : index + 1;
      
      if (nextIndex >= 0 && nextIndex < totalRows) {
        textareaRefs.current[nextIndex]?.focus();
      }
    }
  };

  drag(drop(ref));

  const renderBOMFields = () => (
    <>
      <td className="py-3 px-2 relative">
        {access === "edit" && (
          <div className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0 absolute left-2 top-1/2 transform -translate-y-1/2">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>
        )}
        <div className="ml-6">
          <input
            type="text"
            value={item.component}
            disabled={disableItems || access !== "edit"}
            onChange={(e) => handleUpdateItem(index, { ...item, component: e.target.value })}
            className="w-full p-1 max-w-full border rounded focus:ring-0 text-sm text-gray-800"
            placeholder="Component name"
          />
        </div>
      </td>
      <td className="py-3 px-2">
        <input
          type="text"
          value={item.description}
          disabled={disableItems || access !== "edit"}
          onChange={(e) => handleUpdateItem(index, { ...item, description: e.target.value })}
          className="w-full p-1 border rounded focus:ring-0 text-sm text-gray-600"
          placeholder="Description"
        />
      </td>
      <td className="py-3 px-2">
        <input
          type="number"
          min="1"
          value={item.quantity}
          disabled={disableItems || access !== "edit"}
          onChange={(e) => handleUpdateItem(index, { ...item, quantity: parseInt(e.target.value) || 1 })}
          className="w-full p-1 border rounded focus:ring-0 text-sm text-gray-600 text-center"
          placeholder="1"
        />
      </td>
      <td className="py-3 px-1">
        <div className="relative">
          <span className="text-gray-600 mr-1 flex-shrink-0 absolute left-0 top-1/2 transform -translate-y-1/2">$</span>
          <div className="ml-3">
            <input
              type="number"
              min="0"
              step="0.001"
              value={item.cost}
              disabled={disableItems || access !== "edit"}
              onChange={(e) => {
                let rawValue = e.target.value;
                let normalizedValue = rawValue.replace(',', '.');
                if (/^\d*\.?\d*$/.test(normalizedValue)) {
                  if (
                    normalizedValue.includes('.') &&
                    normalizedValue.startsWith('0') &&
                    !normalizedValue.startsWith('0.')
                  ) {
                    normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                  } else if (!normalizedValue.includes('.') && normalizedValue.length > 1) {
                    normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                  }
                  handleUpdateItem(index, {
                    ...item,
                    cost: normalizedValue
                  });
                }
              }}
              className="w-full p-1 max-w-full border rounded focus:ring-0 text-sm text-gray-600"
              placeholder="0.000"
            />
          </div>
        </div>
      </td>
    </>
  );

  const renderTextareaField = () => (
    <>
      {access === "edit" && (
        <td className="py-3 px-4 relative">
          <div className="mr-2 cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0 absolute top-3.5">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>
        </td> 
      )}
      <td className="py-3 px-4">
        <textarea
          ref={el => {
            if (el && textareaRefs) textareaRefs.current[index] = el;
          }}
          value={item.description}
          onChange={(e) => handleUpdateItem(index, { ...item, description: e.target.value })}
          className={`w-full border rounded focus:ring-0 text-sm text-gray-600 min-h-[80px] max-h-[80px] overflow-y-auto resize-none p-1 ${dataIsLoading ? "blur" : ""}`}
          placeholder="Detailed description"
          onKeyDown={handleTab}
          rows="3"
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
          }}
        />
      </td>
    </>
  );

  const renderMOQFields = () => {
    return (
      <>
        <td className="py-3 px-2 relative">
          {access === "edit" && (
            <div className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0 absolute left-2 top-1/2 transform -translate-y-1/2">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </div>
          )}
          <div className="ml-6">
            <input
              type="number"
              min="1"
              value={item.quantity}
              disabled={disableItems || access !== "edit"}
              onChange={(e) => handleUpdateItem(index, { ...item, quantity: parseInt(e.target.value) || 1 })}
              className="w-full p-1 border rounded focus:ring-0 text-sm text-gray-600 text-left"
              placeholder="1"
            />
          </div>
        </td>
        <td className="py-3 px-1">
          <div className="relative">
            <span className="text-gray-600 mr-1 flex-shrink-0 absolute left-0 top-1/2 transform -translate-y-1/2">$</span>
            <div className="ml-3">
              <input
                type="number"
                min="0"
                step="0.001"
                value={item.cost}
                disabled={disableItems || access !== "edit"}
                onChange={(e) => {
                  let rawValue = e.target.value;
                  let normalizedValue = rawValue.replace(',', '.');
                  if (/^\d*\.?\d*$/.test(normalizedValue)) {
                    if (
                      normalizedValue.includes('.') &&
                      normalizedValue.startsWith('0') &&
                      !normalizedValue.startsWith('0.')
                    ) {
                      normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                    } else if (!normalizedValue.includes('.') && normalizedValue.length > 1) {
                      normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                    }
                    handleUpdateItem(index, {
                      ...item,
                      cost: normalizedValue
                    });
                  }
                }}
                className="w-full p-1 max-w-full border rounded focus:ring-0 text-sm text-gray-600"
                placeholder="0.000"
              />
            </div>
          </div>
        </td>
      </>  
    )
  }

  const getAriaLabel = () => {
    switch(rowType) {
      case "bom":
        return "Remove row"
      case "moq":
        return "Remove row"
      case "instructions":
        return "Remove instruction"
      case "materials":
        return "Remove material"
      default: 
        return "";
    }
  };

  const renderFields = () => {
    switch(rowType) {
      case "bom":
        return renderBOMFields();
      case "materials":
        return renderTextareaField();
      case "instructions":
        return renderTextareaField();
      case "moq":
        return renderMOQFields();
      default: 
        return null;
    }
  }

  return (
    <tr 
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={`
        border-b border-gray-200 
        ${isDragging ? 'bg-gray-100' : 'hover:bg-gray-50'} 
        ${dataIsLoading ? 'blur' : ''}
        ${disableItems ? 'bg-gray-50 pointer-events-none' : ''}
      `}
    >
      {renderFields()}
      {access === "edit" && (
        <td className="py-3 px-2 text-center">
          {!isLastElement && (
            <button 
              onClick={() => {
                if (rowType === 'bom' || rowType === 'moq') {
                  handleRemoveRow(item.id, index);
                } else {
                  handleDeleteItem(item.id, index);
                }
              }}
              className="text-red-500 hover:text-red-700 focus:outline-none inline-flex items-center justify-center disabled:opacity-50"
              aria-label={`${getAriaLabel()}`}
              disabled={disableItems}
              tabIndex={0}
            >
              <TrashIcon className="h-4 w-4 currentColor" />
            </button>
          )}
        </td>
      )}
    </tr>
  );
};

export default DraggableRow; 